import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { 
  getCellIndices, 
  determineCourtForGame, 
  renderGroupTable,
  processGroupData
} from './ScheduleUtils';
import { processBracketData, renderSimpleBracket } from './BracketUtils';
import SearchableBracketWrapper from './SearchableBracketWrapper';

const WomensSchedule = ({ activeSubTab, workbook }) => {
  const [womenData, setWomenData] = useState({});
  const [groupTeams, setGroupTeams] = useState({});
  const [lightDivisionData, setLightDivisionData] = useState({ matches: [], rounds: [] });
  const [hardDivisionData, setHardDivisionData] = useState({ matches: [], rounds: [] });
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (workbook) {
      processWomenWorkbook(workbook);
    }
  }, [workbook]);

  const processWomenWorkbook = (workbook) => {
    try {
      // Process Women's data (Moterys tab)
      const womenSheetIndex = workbook.SheetNames.findIndex(name => 
        name === 'Moterys'
      );
      
      if (womenSheetIndex !== -1) {
        const womenSheet = workbook.Sheets[workbook.SheetNames[womenSheetIndex]];
        
        // Process group stage data
        processWomenData(womenSheet);
        
        // Process bracket data for women - with modified court definitions
        const hardBracket = processWomenBracketData(womenSheet, 'hard');
        const lightBracket = processWomenBracketData(womenSheet, 'light');
        
        setHardDivisionData(hardBracket);
        setLightDivisionData(lightBracket);
      } else {
        setError("Women's sheet not found in workbook");
      }
    } catch (err) {
      console.error("Error processing women's data:", err);
      setError("Failed to process women's tournament data");
    }
  };

  const processWomenData = (worksheet) => {
    // Convert to JSON with header: 1 to get arrays instead of objects
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    
    // Define court ranges for women's division
    const courtRanges = {
      '1 Aikštelė': [
        { startCell: 'C9', endCell: 'F14' }
      ],
      '2 Aikštelė': [
        { startCell: 'J9', endCell: 'M14' }
      ],
      '3 Aikštelė': [
        { startCell: 'Q9', endCell: 'T14' }
      ],
      '4 Aikštelė': [
        { startCell: 'C24', endCell: 'F29' }
      ],
      '5 Aikštelė': [
        { startCell: 'J24', endCell: 'M29' }
      ],
      '6 Aikštelė': [
        { startCell: 'Q24', endCell: 'T29' }
      ]
    };
    
    // Define the group configurations for women's division
    const groupConfigs = {
      'Group A': { 
        teamA: { startCell: 'C9', endCell: 'C14' },
        teamB: { startCell: 'F9', endCell: 'F14' },
        referee: { startCell: 'G9', endCell: 'G14' },
        time: '08:00'
      },
      'Group B': { 
        teamA: { startCell: 'J9', endCell: 'J14' },
        teamB: { startCell: 'M9', endCell: 'M14' },
        referee: { startCell: 'N9', endCell: 'N14' },
        time: '09:00'
      },
      'Group C': { 
        teamA: { startCell: 'Q9', endCell: 'Q14' },
        teamB: { startCell: 'T9', endCell: 'T14' },
        referee: { startCell: 'U9', endCell: 'U14' },
        time: '10:00'
      },
      'Group D': { 
        teamA: { startCell: 'C24', endCell: 'C29' },
        teamB: { startCell: 'F24', endCell: 'F29' },
        referee: { startCell: 'G24', endCell: 'G29' },
        time: '11:00'
      },
      'Group E': { 
        teamA: { startCell: 'J24', endCell: 'J29' },
        teamB: { startCell: 'M24', endCell: 'M29' },
        referee: { startCell: 'N24', endCell: 'N30' },
        time: '12:00'
      },
      'Group F': { 
        teamA: { startCell: 'Q24', endCell: 'Q29' },
        teamB: { startCell: 'T24', endCell: 'T29' },
        referee: { startCell: 'U24', endCell: 'U29' },
        time: '13:00'
      }
    };
    
    // Process group data using shared utility function
    const { grouped, teamObj } = processGroupData(
      groupConfigs, 
      jsonData, 
      courtRanges, 
      getCellIndices, 
      determineCourtForGame
    );
    
    setWomenData(grouped);
    setGroupTeams(prev => ({...prev, women: teamObj}));
  };

  // Custom function to process bracket data with women-specific court definitions
  const processWomenBracketData = (worksheet, bracketType) => {
    try {
      // Convert worksheet to JSON with header: 1 to get arrays instead of objects
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      // Array to store all matches
      const bracketMatches = [];
      
      if (bracketType === 'light') {
        // Define court mapping for women's light bracket
        const lightBracketCourts = {
          // Women Light Division Courts
          "1 Aikštelė": { minCol: 2, maxCol: 5, rowRanges: [[32, 43]] },  // Final
          "2 Aikštelė": { minCol: 9, maxCol: 12, rowRanges: [[32, 43]] }, // Bronze
          "4 Aikštelė": { minCol: 2, maxCol: 5, rowRanges: [[45, 52]] },  // Light bracket rows
          "5 Aikštelė": { minCol: 9, maxCol: 12, rowRanges: [[45, 52]] }, // Light bracket rows
          "6 Aikštelė": { minCol: 16, maxCol: 19, rowRanges: [[45, 52]] } // Light bracket rows
        };
        
        // Function to determine court based on cell position for light bracket
        const determineCourForLightBracket = (row, col) => {
          for (const [courtName, range] of Object.entries(lightBracketCourts)) {
            const { minCol, maxCol, rowRanges } = range;
            
            if (col >= minCol && col <= maxCol) {
              for (const [minRow, maxRow] of rowRanges) {
                if (row >= minRow && row <= maxRow) {
                  return courtName;
                }
              }
            }
          }
          return "Court not specified";
        };
        
        // Custom text mappings
        const customGameTexts = {
          "Game 1": { teamA: "8 Vieta", teamB: "9 Vieta" },
          "Game 2": { teamA: "5 Vieta", teamB: "12 Vieta" },
          "Game 3": { teamA: "6 Vieta", teamB: "11 Vieta" },
          "Game 4": { teamA: "7 Vieta", teamB: "10 Vieta" },
          "Game 5": { teamA: "Game 1 Winner", teamB: "1 Vieta" },
          "Game 6": { teamA: "Game 2 Winner", teamB: "4 Vieta" },
          "Game 7": { teamA: "Game 3 Winner", teamB: "3 Vieta" },
          "Game 8": { teamA: "Game 4 Winner", teamB: "2 Vieta" },
          "Game 9": { teamA: "Game 5 Looser", teamB: "Game 4 Looser" },
          "Game 10": { teamA: "Game 3 Looser", teamB: "Game 6 Looser" },
          "Game 11": { teamA: "Game 7 Looser", teamB: "Game 2 Looser" },
          "Game 12": { teamA: "Game 1 Looser", teamB: "Game 8 Looser" },
          "Game 13": { teamA: "Game 6 Winner", teamB: "Game 5 Winner" },
          "Game 14": { teamA: "Game 8 Winner", teamB: "Game 7 Winner" },
          "Game 15": { teamA: "Game 12 Winner", teamB: "Game 11 Winner" },
          "Game 16": { teamA: "Game 9 Winner", teamB: "Game 10 Winner" },
          "Game 17": { teamA: "Game 15 Winner", teamB: "Game 14 Looser" },
          "Game 18": { teamA: "Game 16 Winner", teamB: "Game 13 Looser" },
          "Game 19": { teamA: "Game 13 Winner", teamB: "Game 17 Winner" },
          "Game 20": { teamA: "Game 18 Winner", teamB: "Game 16 Winner" },
          "Final": { teamA: "Game 19 Winner", teamB: "Game 20 Winner" },
          "Bronze": { teamA: "Game 20 Looser", teamB: "Game 19 Loser" },
        };
        
        const lightDivisionMatches = [
          // Game 1: C46 vs F46 scores D46, E46, referee G46
          { row: 45, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 1" },
          // Game 2: J46 vs M46 scores K46, L46, referee N46
          { row: 45, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 2" },
          // Game 3: Q46 vs T46 scores R46, S46, referee U46
          { row: 45, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 3" },
          // Game 4: Q47 vs T47 scores R47, S47, referee U47
          { row: 46, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 4" },
          // Game 5: C47 vs F47 scores D47, E47, referee G47
          { row: 46, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 5" },
          // Game 6: J47 vs M47 scores K47, L47, referee N47
          { row: 46, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 6" },
          // Game 7: J48 vs M48 scores K48, L48, referee N48
          { row: 47, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 7" },
          // Game 8: Q48 vs T48 scores R48, S48, referee U48
          { row: 47, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 8" },
          // Game 9: C48 vs F48 scores D48, E48, referee G48
          { row: 47, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 9" },
          // Game 10: Q49 vs T49 scores R49, S49, referee U49
          { row: 48, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 10" },
          // Game 11: J49 vs M49 scores K49, L49, referee N49
          { row: 48, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 11" },
          // Game 12: C49 vs F49 scores D49, E49, referee G49
          { row: 48, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 12" },
          // Game 13: J50 vs M50 scores K50, L50, referee N50
          { row: 49, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 13" },
          // Game 14: Q50 vs T50 scores R50, S50, referee U50
          { row: 49, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 14" },
          // Game 15: C50 vs F50 scores D50, E50, referee G50
          { row: 49, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 15" },
          // Game 16: J51 vs M51 scores K51, L51, referee N51
          { row: 50, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 16" },
          // Game 17: C51 vs F51 scores D51, E51, referee G51
          { row: 50, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 17" },
          // Game 18: J52 vs M52 scores K52, L52, referee N52
          { row: 51, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 18" },
          // Game 19: C52 vs F52 scores D52, E52, referee G52
          { row: 51, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 19" },
          // Game 20 (moved to 2nd court): J40 vs M40 scores K40, L40, referee N40
          { row: 39, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 20" },
          // Bronze: J41 vs M41 scores K41, L41, referee N41
          { row: 40, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Bronze" },
          // Final: C41 vs F41 scores D41, E41, referee G41
          { row: 40, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Final" }
        ];
        
        // Process each match definition
        lightDivisionMatches.forEach((matchDef, index) => {
          const { row, teamACol, teamAScoreCol, teamBScoreCol, teamBCol, refereeCol, gameNumber } = matchDef;
          
          // Get time from column A
          const time = formatTimeValue(getCellValue(jsonData, row, 0) || "");
          
          // Get all other values
          let teamA = getCellValue(jsonData, row, teamACol);
          const teamAScore = getCellValue(jsonData, row, teamAScoreCol);
          const teamBScore = getCellValue(jsonData, row, teamBScoreCol);
          let teamB = getCellValue(jsonData, row, teamBCol);
          const referee = getCellValue(jsonData, row, refereeCol) || "TBD";
          
          // Apply custom text if team is "TBD" or missing
          if (customGameTexts[gameNumber]) {
            // Apply custom teamA text if available and team is not defined in Excel
            if (customGameTexts[gameNumber].teamA && (!teamA || teamA === "TBD")) {
              teamA = customGameTexts[gameNumber].teamA;
            }
            
            // Apply custom teamB text if available and team is not defined in Excel
            if (customGameTexts[gameNumber].teamB && (!teamB || teamB === "TBD")) {
              teamB = customGameTexts[gameNumber].teamB;
            }
          }
          
          // Use default "TBD" if no custom text and no Excel value
          teamA = teamA || "TBD";
          teamB = teamB || "TBD";
          
          // Determine court dynamically
          const court = determineCourForLightBracket(row, teamACol);
          
          // Create the match object
          const match = {
            id: (index + 1).toString(),
            gameNumber,
            time,
            teamA,
            teamAScore: teamAScore !== undefined ? teamAScore : "",
            teamB,
            teamBScore: teamBScore !== undefined ? teamBScore : "",
            court,
            referee
          };
          
          bracketMatches.push(match);
        });
      } 
      else if (bracketType === 'hard') {
        // Define court mapping for women's hard bracket
        const hardBracketCourts = {
          // Women Hard Division Courts
          "1 Aikštelė": { minCol: 2, maxCol: 7, rowRanges: [[32, 43]] },
          "2 Aikštelė": { minCol: 9, maxCol: 12, rowRanges: [[32, 43]] },
          "3 Aikštelė": { minCol: 16, maxCol: 19, rowRanges: [[32, 43]] }
        };
        
        // Function to determine court based on cell position for hard bracket
        const determineCourForHardBracket = (row, col) => {
          for (const [courtName, range] of Object.entries(hardBracketCourts)) {
            const { minCol, maxCol, rowRanges } = range;
            
            if (col >= minCol && col <= maxCol) {
              for (const [minRow, maxRow] of rowRanges) {
                if (row >= minRow && row <= maxRow) {
                  return courtName;
                }
              }
            }
          }
          return "Court not specified";
        };
        
        // Custom text mappings
        const customGameTexts = {
          "Game 1": { teamA: "8 Vieta", teamB: "9 Vieta" },
          "Game 2": { teamA: "5 Vieta", teamB: "12 Vieta" },
          "Game 3": { teamA: "6 Vieta", teamB: "11 Vieta" },
          "Game 4": { teamA: "7 Vieta", teamB: "10 Vieta" },
          "Game 5": { teamA: "Game 1 Winner", teamB: "1 Vieta" },
          "Game 6": { teamA: "Game 2 Winner", teamB: "4 Vieta" },
          "Game 7": { teamA: "Game 3 Winner", teamB: "3 Vieta" },
          "Game 8": { teamA: "Game 4 Winner", teamB: "2 Vieta" },
          "Game 9": { teamA: "Game 5 Looser", teamB: "Game 4 Looser" },
          "Game 10": { teamA: "Game 3 Looser", teamB: "Game 6 Looser" },
          "Game 11": { teamA: "Game 7 Looser", teamB: "Game 2 Looser" },
          "Game 12": { teamA: "Game 1 Looser", teamB: "Game 8 Looser" },
          "Game 13": { teamA: "Game 6 Winner", teamB: "Game 5 Winner" },
          "Game 14": { teamA: "Game 8 Winner", teamB: "Game 7 Winner" },
          "Game 15": { teamA: "Game 12 Winner", teamB: "Game 11 Winner" },
          "Game 16": { teamA: "Game 9 Winner", teamB: "Game 10 Winner" },
          "Game 17": { teamA: "Game 15 Winner", teamB: "Game 14 Looser" },
          "Game 18": { teamA: "Game 16 Winner", teamB: "Game 13 Looser" },
          "Game 19": { teamA: "Game 13 Winner", teamB: "Game 17 Winner" },
          "Game 20": { teamA: "Game 18 Winner", teamB: "Game 16 Winner" },
          "Final": { teamA: "Game 19 Winner", teamB: "Game 20 Winner" },
          "Bronze": { teamA: "Game 20 Looser", teamB: "Game 19 Loser" },
        };
        
        const hardDivisionMatches = [
          // Game 1: C33 vs F33 scores D33, E33, referee G33
          { row: 32, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 1" },
          // Game 2: J33 vs M33 scores K33, L33, referee N33
          { row: 32, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 2" },
          // Game 3: Q33 vs T33 scores R33, S33, referee U33
          { row: 32, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 3" },
          // Game 4: Q34 vs T34 scores R34, S34, referee U34
          { row: 33, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 4" },
          // Game 5: C34 vs F34 scores D34, E34, referee G34
          { row: 33, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 5" },
          // Game 6: J34 vs M34 scores K34, L34, referee N34
          { row: 33, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 6" },
          // Game 7: J35 vs M35 scores K35, L35, referee N35
          { row: 34, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 7" },
          // Game 8: Q35 vs T35 scores R35, S35, referee U35
          { row: 34, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 8" },
          // Game 9: C35 vs F35 scores D35, E35, referee G35
          { row: 34, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 9" },
          // Game 10: Q36 vs T36 scores R36, S36, referee U36
          { row: 35, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 10" },
          // Game 11: J36 vs M36 scores K36, L36, referee N36
          { row: 35, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 11" },
          // Game 12: C36 vs F36 scores D36, E36, referee G36
          { row: 35, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 12" },
          // Game 13: J37 vs M37 scores K37, L37, referee N37
          { row: 36, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 13" },
          // Game 14: Q37 vs T37 scores R37, S37, referee U37
          { row: 36, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 14" },
          // Game 15: C37 vs F37 scores D37, E37, referee G37
          { row: 36, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 15" },
          // Game 16: J38 vs M38 scores K38, L38, referee N38
          { row: 37, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 16" },
          // Game 17: C38 vs F38 scores D38, E38, referee G38
          { row: 37, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 17" },
          // Game 18: J39 vs M39 scores K39, L39, referee N39
          { row: 38, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 18" },
          // Game 19: C39 vs F39 scores D39, E39, referee G39
          { row: 38, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 19" },
          // Game 20: C40 vs F40 scores D40, E40, referee G40
          { row: 39, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 20" },
          // Bronze: J42 vs M42 scores K42, L42, referee N42
          { row: 41, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Bronze" },
          // Final: C42 vs F42 scores D42, E42, referee G42
          { row: 41, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Final" }
        ];
        
        
        // Process each match definition
        hardDivisionMatches.forEach((matchDef, index) => {
          const { row, teamACol, teamAScoreCol, teamBScoreCol, teamBCol, refereeCol, gameNumber} = matchDef;
          
          // Get time from column A
          const time = formatTimeValue(getCellValue(jsonData, row, 0) || "");
          
          // Get all other values
          let teamA = getCellValue(jsonData, row, teamACol);
          const teamAScore = getCellValue(jsonData, row, teamAScoreCol);
          const teamBScore = getCellValue(jsonData, row, teamBScoreCol);
          let teamB = getCellValue(jsonData, row, teamBCol);

          const referee = getCellValue(jsonData, row, refereeCol) || "TBD";
          
          // Apply custom text if team is "TBD" or missing
          if (customGameTexts[gameNumber]) {
            // Apply custom teamA text if available and team is not defined in Excel
            if (customGameTexts[gameNumber].teamA && (!teamA || teamA === "TBD")) {
              teamA = customGameTexts[gameNumber].teamA;
            }
            
            // Apply custom teamB text if available and team is not defined in Excel
            if (customGameTexts[gameNumber].teamB && (!teamB || teamB === "TBD")) {
              teamB = customGameTexts[gameNumber].teamB;
            }
          }
          
          // Use default "TBD" if no custom text and no Excel value
          teamA = teamA || "TBD";
          teamB = teamB || "TBD";
          
          // Determine court dynamically
          const court = determineCourForHardBracket(row, teamACol);
          
          // Create the match object
          const match = {
            id: (index + 1).toString(),
            gameNumber,
            time,
            teamA,
            teamAScore: teamAScore !== undefined ? teamAScore : "",
            teamB,
            teamBScore: teamBScore !== undefined ? teamBScore : "",
            court,
            referee
          };
          
          bracketMatches.push(match);
        });
      }

      return { matches: bracketMatches, rounds: [] };
    } catch (err) {
      console.error("Error processing bracket data:", err);
      return { matches: [], rounds: [] };
    }
  };

  // Helper function to format time values
  const formatTimeValue = (value) => {
    if (value instanceof Date) {
      // Format as HH:MM
      return value.getHours().toString().padStart(2, '0') + ':' + 
             value.getMinutes().toString().padStart(2, '0');
    }
    return value;
  };

  // Helper function to safely get cell value
  const getCellValue = (jsonData, rowIdx, colIdx, refereeCol) => {
    if (jsonData && jsonData[rowIdx] && colIdx >= 0 && colIdx < jsonData[rowIdx].length) {
      return jsonData[rowIdx][colIdx];
    }
    return null;
  };

  if (loading) {
    return <div className="loading">Loading women's tournament schedule...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  // Choose which content to display based on activeSubTab
  const renderTabContent = () => {
    switch(activeSubTab) {
      case 'group':
        return (
          <div className="tab-content active" id="group-tab">
            <h2>Group Stage Matches - Women</h2>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <label htmlFor="groupFilter"><strong>Filter by Group:</strong></label>
              <select id="groupFilter" value={filter} onChange={(e) => setFilter(e.target.value)}>
                <option value="all">All</option>
                {Object.keys(womenData).map(group => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </select>
            </div>
            {Object.entries(womenData).map(([groupName, matches]) =>
              renderGroupTable(groupName, matches, groupTeams, 'women', filter)
            )}
            {Object.keys(womenData).length === 0 && (
              <div className="no-data-message">
                <p>No women's group stage data available yet.</p>
              </div>
            )}
          </div>
        );
      case 'light':
        return (
          <div className="tab-content active" id="light-tab">
            {lightDivisionData.matches && lightDivisionData.matches.length > 0 ? (
              <SearchableBracketWrapper 
                bracketData={lightDivisionData} 
                divisionName="Light Division - Women" 
              />
            ) : (
              <div>
                <h2 className="section-title">Light Division Bracket - Women</h2>
                <div className="no-data-message">
                  <p>Light division bracket data will be available closer to the tournament.</p>
                </div>
              </div>
            )}
          </div>
        );
      case 'hard':
        return (
          <div className="tab-content active" id="hard-tab">
            {hardDivisionData.matches && hardDivisionData.matches.length > 0 ? (
              <SearchableBracketWrapper 
                bracketData={hardDivisionData} 
                divisionName="Hard Division - Women" 
              />
            ) : (
              <div>
                <h2 className="section-title">Hard Division Bracket - Women</h2>
                <div className="no-data-message">
                  <p>Hard division bracket data will be available closer to the tournament.</p>
                </div>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {renderTabContent()}
    </>
  );
};

export default WomensSchedule;